import { Box, Button, Group, Radio, Stack, Text, Textarea, TextInput } from '@mantine/core'
import { MIME_TYPES } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import { IconDeviceFloppy, IconExternalLink, IconLink } from '@tabler/icons-react'
import type { MRT_ColumnDef } from 'mantine-react-table'
import { useEffect, useState } from 'react'
import type { z } from 'zod'

import type { FileUploadProps, UploadPhase } from '@/components/FileUpload'
import {
  acceptedFileUploadText,
  ALLOWED_IMAGE_TYPES,
  contentUploadMimeTypes,
  FileUpload,
  needsExtractionMimeTypes,
  supportsProgressMimeTypes,
} from '@/components/FileUpload'
import { RadioCard } from '@/components/RadioCard'
import { Table } from '@/components/Table'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import type { ResourceDataContentModalProps } from '@/screens/ContentModuleFormScreen/ResourceDataContentModal'
import { ResourceDataContentModal } from '@/screens/ContentModuleFormScreen/ResourceDataContentModal'
import { FileProcessingError } from '@/services/api/api'
import type { ProcessProgress } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import * as classes from '@/styles/ContentModuleFormScreen.css'
import type { ResourceFormSchema, ResourceModalType } from '@/utils/contentModuleForm'
import { castZodFormToResource, resourceSchema } from '@/utils/contentModuleForm'
import { getRequiredFieldDescriptionProps } from '@/utils/forms'
import { getYouTubeUrlError } from '@/utils/urlValidation'

type ResourceZodType = z.infer<ResourceFormSchema>

export type ResourceModalProps = {
  type: 'cm' | 'step'
}

const fileUploadProps: Record<ResourceModalType, Partial<FileUploadProps>> = {
  text: {
    titles: { idle: 'Upload File' },
    accept: contentUploadMimeTypes,
    primaryActionLabel: 'Select File',
  },
  url: {},
  video: {
    titles: { idle: 'Upload Video' },
    accept: [MIME_TYPES.mp4],
    primaryActionLabel: 'Select Video',
  },
  image: {
    titles: { idle: 'Upload Image' },
    accept: [...ALLOWED_IMAGE_TYPES],
    primaryActionLabel: 'Select Image',
  },
}

export function ResourceModal(props: CMFormSection & ResourceModalProps) {
  const { configStore } = useGlobalState()

  // Upload phase tracking - explicit state machine to prevent UI flashing
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle')
  const [processProgressPct, setProcessProgressPct] = useState<number>(0)

  // Use local state to track resourceId so component re-renders when it changes
  const [resourceId, setResourceId] = useState<string | undefined>(props.resourceId)

  // Track the type that the current data belongs to (set on load for existing resources, and on upload for new files)
  const [uploadedDataType, setUploadedDataType] = useState<string | undefined>(() => {
    const formValues = props.resourceForm.getValues()
    // If there's existing data for a video/image, track its type
    if (formValues.resourceId && (formValues.type === 'video' || formValues.type === 'image') && formValues.data) {
      return formValues.type
    }
    return undefined
  })

  // Sync local resourceId state with form value when it changes externally (e.g., from onSuccess handler)
  const formResourceId = props.resourceForm.getValues().resourceId
  useEffect(() => {
    if (formResourceId && formResourceId !== resourceId) {
      setResourceId(formResourceId)
    }
  }, [formResourceId, resourceId])

  // Check if this is a new resource and if we should show the text option
  const isNewResource = !resourceId
  const shouldShowTextOption = !isNewResource && props.resourceForm.getValues().type === 'text'

  const { uploading, uploadProgress, upload } = useFileUpload({
    onPrepareError: () => {
      props.resourceForm.setFieldError('file', 'Something went wrong. Please try again or contact support.')
      setUploadPhase('idle')
    },
    onUploadError: () => {
      props.resourceForm.setFieldError('file', 'Upload failed. Please try again or contact support.')
      setUploadPhase('idle')
    },
    onUploadSuccess: () => props.resourceForm.setFieldError('file', undefined),
  })

  function openResourceContentModal() {
    props.openModal?.({
      title: `${!props.resourceForm.getValues().data ? 'New' : 'Edit'} Resource Content Entry`,
      subtitle: 'Copy and paste or enter your content manually.',
      Component: ResourceDataContentModal,
      ComponentProps: { onSubmit: submitResource } satisfies ResourceDataContentModalProps,
    })
  }

  async function validateAndSubmitResource() {
    const values = props.resourceForm.getValues()
    const validate = props.resourceForm.validateField

    const validations = [
      validate('type'),
      validate('title'),
      validate('description'),
      (values.type === 'url' || values.type === 'youtube') && validate('dataUrl'),
      values.type === 'text' && (values.file ? validate('file') : validate('data')),
      (values.type === 'image' || values.type === 'video') && validate('data'),
    ].filter(Boolean)

    if (validations.some(v => v && v.hasError)) {
      return
    }

    if (values.type === 'url' || values.type === 'youtube') {
      const youtubeError = getYouTubeUrlError(values.dataUrl)
      if (youtubeError) {
        props.resourceForm.setFieldError('dataUrl', youtubeError)
        return
      }
    }

    await submitResource()
    props.closeModal()
  }

  function onProgress(progress: ProcessProgress['processProgress']) {
    const current = progress?.current ?? 0
    const total = progress?.total ?? 0
    // Avoid NaN on divide by zero
    const fraction = current / total || 0
    const progressPct = fraction * 100

    console.log(`ResourceModal:onProgress ${progressPct}`)
    setProcessProgressPct(progressPct)
  }

  async function submitResource() {
    const values = props.resourceForm.getValues()
    const file = values.file

    // This is passed as a form field from the previous step, which may have uploaded this file.
    // We need to remove it from the params so it's not included in the mutation.
    if (file) {
      values.file = null
    }

    // Resources are always approved since there's no AI approval flow
    values.isApproved = true
    props.resourceForm.setFieldValue('isApproved', true)

    const idParams = props.type === 'cm' ? { task_id: props.taskId! } : { step_id: props.stepId! }
    const params = castZodFormToResource(values)

    setUploadPhase('processing')
    setProcessProgressPct(0)

    try {
      if (resourceId) {
        // update
        if (needsExtractionMimeTypes.includes(file?.type ?? '')) {
          if (supportsProgressMimeTypes.includes(file?.type ?? '')) {
            await props.resourceUpdateMutation?.mutateAsync({
              ...params,
              ...idParams,
              resource_id: resourceId,
              onProgress,
            })
          } else {
            // We fake our processing progress for filetypes that don't support progress feedback
            setProcessProgressPct(50)
            await props.resourceUpdateMutation?.mutateAsync({ ...params, ...idParams, resource_id: resourceId })
          }
        } else {
          setProcessProgressPct(50)
          await props.resourceUpdateMutation?.mutateAsync({ ...params, ...idParams, resource_id: resourceId })
        }
      } else {
        // create
        let response
        if (needsExtractionMimeTypes.includes(file?.type ?? '')) {
          if (supportsProgressMimeTypes.includes(file?.type ?? '')) {
            response = await props.resourceCreateMutation?.mutateAsync({ ...params, ...idParams, onProgress })
          } else {
            // We fake our processing progress for filetypes that don't support progress feedback
            setProcessProgressPct(50)
            response = await props.resourceCreateMutation?.mutateAsync({ ...params, ...idParams })
          }
        } else {
          setProcessProgressPct(50)
          response = await props.resourceCreateMutation?.mutateAsync({ ...params, ...idParams })
        }

        if (response?.resourceId) {
          props.resourceForm.setFieldValue('resourceId', response.resourceId)
          setResourceId(response.resourceId)
        }
      }
    } catch (error) {
      if (error instanceof FileProcessingError) {
        const msg =
          'There was an issue processing your file. This may be due to the file being too large or in an unsupported format. Please try a smaller file or contact support.'
        props.resourceForm.setFieldError('file', msg)
        notifications.show({
          title: 'File processing failed',
          message: msg,
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      } else {
        throw error
      }
    } finally {
      setUploadPhase('idle')
    }
  }

  const isMutating = props.resourceCreateMutation?.isPending || props.resourceUpdateMutation?.isPending
  const currentType = props.resourceForm.getValues().type
  const currentData = props.resourceForm.getValues().data
  // Show preview only when current type matches the type the data was uploaded for
  const hasFileData =
    (currentType === 'video' || currentType === 'image') && !!currentData && currentType === uploadedDataType

  const formValues = props.resourceForm.getValues()
  const hasRequiredFields = formValues.title?.trim() && formValues.description?.trim()
  const hasResourceData =
    ((formValues.type === 'url' || formValues.type === 'youtube') && formValues.dataUrl?.trim()) ||
    (formValues.type === 'text' && (formValues.data || formValues.s3File)) ||
    ((formValues.type === 'image' || formValues.type === 'video') && formValues.data)
  const canSave = hasRequiredFields && hasResourceData

  return (
    <Stack>
      <Radio.Group
        withAsterisk
        label="Type"
        description="The type determines how the resource will be used."
        {...props.resourceForm.getInputProps('type')}
        onChange={e => {
          props.resourceForm.setFieldValue('type', e as z.infer<ResourceFormSchema>['type'])

          props.resourceForm.setFieldError('data', '')
          props.resourceForm.setFieldError('dataUrl', '')
          props.resourceForm.setFieldError('file', '')

          props.metadataForm.setFieldValue('resource', undefined)
        }}>
        <Group justify="space-between" gap="xl" align="stretch">
          {shouldShowTextOption && (
            <RadioCard
              label="Additional Information"
              description="Sidekick will commit this information to memory and share with users when appropriate."
              value={resourceSchema.shape.type.enum.text}
            />
          )}
          <RadioCard
            label="Supplemental URL"
            description="Sidekick will display this website for users when appropriate."
            value={resourceSchema.shape.type.enum.url}
          />
          <RadioCard
            label="Supplemental Video"
            description="Sidekick will play this video file for users when appropriate."
            value={resourceSchema.shape.type.enum.video}
          />
          <RadioCard
            label="Supplemental Image"
            description="Sidekick will display this image file for users when appropriate."
            value={resourceSchema.shape.type.enum.image}
          />
        </Group>
      </Radio.Group>

      {props.resourceForm.getValues().type === 'url' || props.resourceForm.getValues().type === 'youtube' ? (
        <TextInput
          withAsterisk
          label="URL"
          placeholder="https://example.com/resource"
          size="md"
          leftSection={<IconLink size={18} />}
          radius="md"
          {...props.resourceForm.getInputProps('dataUrl')}
        />
      ) : (
        <FileUpload
          withAsterisk
          label="Resource File"
          activateOnClick={false}
          loading={uploading || isMutating}
          uploadPhase={uploadPhase}
          uploadProgress={uploadProgress}
          processProgress={processProgressPct}
          maxFiles={1}
          error={
            props.resourceForm.getValues().type !== 'url' && props.resourceForm.getValues().type !== 'youtube'
              ? props.resourceForm.getInputProps('file').error || props.resourceForm.getInputProps('data').error
              : undefined
          }
          {...fileUploadProps[(props.resourceForm.getValues().type ?? 'text') as ResourceModalType]}
          secondaryActionLabel={props.resourceForm.getValues().type === 'text' ? 'Enter Manually' : undefined}
          onSecondaryActionClick={openResourceContentModal}
          hints={{
            idle: (
              <span style={{ whiteSpace: 'pre-line' }}>
                {props.resourceForm.getValues().type === 'text'
                  ? acceptedFileUploadText
                  : `Drag and drop media files here or click below to browse.`}
              </span>
            ),
          }}
          onDrop={async files => {
            props.resourceForm.setFieldValue('file', null)
            props.resourceForm.setFieldValue('data', '')
            props.resourceForm.setFieldValue('fileName', '')

            props.resourceForm.setFieldError('file', '')
            props.resourceForm.setFieldError('data', '')

            const file = files[0]
            const currentType = props.resourceForm.getValues().type

            setUploadPhase('uploading')

            if (currentType === 'text') {
              const uploadResponse = await upload(file)
              if (!uploadResponse) {
                setUploadPhase('idle')
                return
              }

              // Store file reference for mimetype checking during submit (for progress tracking)
              props.resourceForm.setFieldValue('file', file)
              props.resourceForm.setFieldValue('s3File', uploadResponse.filePath)
              props.resourceForm.setFieldValue('fileName', file.name)
            } else {
              const uploadResponse = await upload(file)
              if (!uploadResponse) {
                setUploadPhase('idle')
                return
              }

              props.resourceForm.setFieldValue('data', uploadResponse.filePath)
              props.resourceForm.setFieldValue('fileName', file.name)
              // Track which type this data was uploaded for
              setUploadedDataType(currentType)
            }

            setUploadPhase('idle')
          }}
          BottomSlot={
            hasFileData && (
              <Table
                data={hasFileData ? [props.resourceForm.getValues()] : []}
                columns={contentCols}
                rowActionsInclude={row => {
                  if (row.original.type === 'text') {
                    return ['edit']
                  } else {
                    return ['view']
                  }
                }}
                enableTopToolbar={false}
                enableTableHead={false}
                mantinePaperProps={{ className: classes.fileUploadTable }}
                onRowActionClick={(action, row) => {
                  if (action === 'edit') {
                    openResourceContentModal()
                  } else if (action === 'view') {
                    window.open(`${configStore.serverBaseUrl}/files/${row.original.data}`, '_blank')
                  }
                }}
                displayColumnDefOptions={{ 'mrt-row-actions': { size: 60 } }}
              />
            )
          }
        />
      )}
      {(uploadPhase !== 'idle' || isMutating) && (
        <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} mt="sm">
          <Text c="dimmed" className={classes.inputDescription}>
            {uploadPhase === 'uploading' && 'Uploading file...'}
            {uploadPhase === 'processing' &&
              'Processing content can take some time, depending on your bandwidth. Files over 1 gigabyte can potentially take hours.'}
            {uploadPhase === 'idle' && isMutating && 'Saving...'}
          </Text>
        </Box>
      )}

      <Stack gap="lg" mt="xl">
        <TextInput
          withAsterisk
          label="Title"
          radius="md"
          {...props.resourceForm.getInputProps('title')}
          {...getRequiredFieldDescriptionProps(props.resourceForm, 'title', 'Title of the resource.')}
        />

        <Textarea
          withAsterisk
          label="Description"
          autosize
          minRows={4}
          radius="md"
          {...props.resourceForm.getInputProps('description')}
          {...getRequiredFieldDescriptionProps(props.resourceForm, 'description', 'Brief description of file content.')}
        />
      </Stack>

      <Group gap="lg" justify="flex-end" mb="lg">
        <Button variant="light" color="gray" onClick={() => props.closeModal()} disabled={isMutating}>
          Cancel
        </Button>

        <Button
          leftSection={<IconDeviceFloppy />}
          loading={isMutating || uploadPhase !== 'idle'}
          disabled={!canSave}
          onClick={validateAndSubmitResource}>
          Save Supplemental Resource
        </Button>
      </Group>
    </Stack>
  )
}

const contentCols: MRT_ColumnDef<NonNullable<ResourceZodType>>[] = [
  {
    id: 'content',
    header: '',
    grow: true,
    Cell: props => {
      const { configStore } = useGlobalState()
      const { type } = props.row.original

      if (type === 'text') {
        return (
          <Text size="sm" lineClamp={2} style={{ whiteSpace: 'pre-wrap' }}>
            {props.row.original.data}
          </Text>
        )
      } else {
        return (
          <Group gap="xl">
            <Button
              rightSection={<IconExternalLink size={16} />}
              color="indigo.3"
              variant="filled"
              component="a"
              size="xs"
              tt="uppercase"
              target="_blank"
              href={`${configStore.serverBaseUrl}/files/${props.row.original.data}`}>
              Preview {type}
            </Button>

            <Text size="sm" fw={500} lineClamp={2}>
              {props.row.original.fileName}
            </Text>
          </Group>
        )
      }
    },
  },
]

// Removed per Jeff's request
// {props.resourceForm.getValues().type !== 'text' && (
//   <>
//     <Grid.Col span={6}>
//       <Textarea
//         withAsterisk
//         label={<ComingSoon prefix="ESK Intro " />}
//         description="This is how the file will be introduced to the ESK."
//         autosize
//         resize="vertical"
//         flex={1}
//         minRows={8}
//         rightSectionProps={{ className: commonStyles.textareaRightSectionWithAIActionButton }}
//         rightSection={
//           <AIActionButton
//             variant="action-button"
//             onClick={() => chatStore.submitMockSMESKChatFn?.('This feature is coming soon!')}
//           />
//         }
//       />
//     </Grid.Col>

//     <Grid.Col span={6}>
//       <Textarea
//         label={<ComingSoon prefix="Purpose " />}
//         description="The purpose of the file and how it will be used."
//         autosize
//         resize="vertical"
//         flex={1}
//         minRows={8}
//         rightSectionProps={{ className: commonStyles.textareaRightSectionWithAIActionButton }}
//         rightSection={
//           <AIActionButton
//             variant="action-button"
//             onClick={() => chatStore.submitMockSMESKChatFn?.('This feature is coming soon!')}
//           />
//         }
//       />
//     </Grid.Col>
//   </>
// )}
